import { IconMail, IconMailForward } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Alert, Button, Card, Heading, Input, Stack, Text } from "@/components/ui";
import { forgotPassword } from "@/shared/api/auth.api";

const schema = z.object({ email: z.string().email("Email inválido") });

/**
 * `/recuperar-contrasena` (pública) · pide el correo y solicita el enlace de
 * recuperación. El backend SIEMPRE responde éxito (anti-enumeración), así
 * que el mensaje al usuario es neutro: "si el correo está registrado…".
 */
function RecuperarContrasenaPage() {
  const [enviado, setEnviado] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | undefined>();

  const solicitar = useMutation({
    mutationFn: (email: string) => forgotPassword(email),
    onSuccess: () => setEnviado(true),
    // El backend nunca revela si el correo existe · un error aquí solo puede
    // ser de red/rate-limit; mostramos el mismo mensaje neutro.
    onError: () => setEnviado(true),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message);
      return;
    }
    setError(undefined);
    solicitar.mutate(parsed.data.email);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card padding="xl" className="w-full max-w-110">
        <Stack gap="md">
          <div>
            <Heading order={3}>Recuperar contraseña</Heading>
            <Text size="sm" c="dimmed" className="mt-1">
              Escribe el correo con el que entras al portal y te enviaremos un enlace para restablecer tu contraseña.
            </Text>
          </div>

          {enviado ? (
            <Alert variant="success" icon={<IconMailForward size={18} />}>
              Si el correo está registrado en el portal, recibirás un mensaje con el enlace de recuperación en unos
              minutos. Revisa también tu bandeja de spam.
            </Alert>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              <Stack gap="sm">
                <Input
                  label="Correo electrónico"
                  placeholder="tu.correo@multiva.test"
                  autoComplete="email"
                  leftSection={<IconMail size={16} stroke={1.6} />}
                  size="md"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={error}
                />
                <Button type="submit" fullWidth loading={solicitar.isPending}>
                  Enviar enlace de recuperación
                </Button>
              </Stack>
            </form>
          )}

          <div className="flex justify-center">
            <Link to="/login" className="text-sm text-multiva-600 hover:underline">
              Volver al inicio de sesión
            </Link>
          </div>
        </Stack>
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/recuperar-contrasena")({
  component: RecuperarContrasenaPage,
});
